import { useState } from 'react';
import type { PlaneFinderApp } from '../app/PlaneFinderApp';
import type { PlaneFinderState } from './usePlaneFinder';

// The Flatness slider is a normalized 0–100% (matching the cull sliders) over the
// detection angle tolerance: 100% = strict (4° — only near-perfectly-flat accepted),
// 0% = loose (30° — grows over bumps and curves). The app API stays in degrees.
const DEG_STRICT = 4; // 100% flat
const DEG_LOOSE = 30; // 0% flat
const degToFlatness = (deg: number) => Math.round(((DEG_LOOSE - deg) / (DEG_LOOSE - DEG_STRICT)) * 100);
const flatnessToDeg = (pct: number) => DEG_LOOSE - (pct / 100) * (DEG_LOOSE - DEG_STRICT);

/**
 * The capture panel (top of the left column) — a transcoder-style card, always
 * visible. Capture button first, then the flatness + cull sliders. Until a flat
 * surface is picked the panel is disabled (greyed). Capture resolution is
 * auto-matched to the source texture's detail (no quality dial); pressing Capture
 * downloads the PNG immediately (no review). The framed region's dimensions live
 * in the DimsOverlay on the 3D view, not here.
 */
export function ControlPanel({ app, state }: { app: PlaneFinderApp; state: PlaneFinderState }) {
  // Seed from the app so the controls reflect the live values across remounts.
  const [flatness, setFlatness] = useState<number>(() => degToFlatness(app.sensitivity));
  const [cullFront, setCullFront] = useState<number>(() => app.cullFront);
  const [cullBack, setCullBack] = useState<number>(() => app.cullBack);
  const region = state.region;
  const capturing = state.stage === 'capturing';
  const disabled = !region; // no flat surface selected yet

  return (
    <div className={`asset pf-card${disabled ? ' pf-card-disabled' : ''}`}>
      <div className="asset-hd">
        <span className="asset-title">Plane Finder</span>
      </div>
      <div className="pf-bd">
        <button
          className="start"
          disabled={capturing || disabled}
          onClick={() => void app.capture()}
        >
          {capturing ? 'Capturing…' : 'Capture'}
        </button>
        {capturing && (
          // Indeterminate: the capture is a single render+readback (no granular
          // progress exists), so an animated sweep is honest — a pct-driven bar
          // would sit at 0% the whole time.
          <div className="bar bar-indeterminate">
            <div />
          </div>
        )}

        <div className="pf-ctrl">
          <div className="pf-ctrl-hd">
            <label>Flatness</label>
            <span className="pf-ctrl-val">{flatness}%</span>
          </div>
          <input
            className="pf-slider"
            type="range"
            min={0}
            max={100}
            step={1}
            value={flatness}
            disabled={capturing || disabled}
            onChange={(e) => {
              const v = Number(e.target.value);
              setFlatness(v);
              app.setSensitivity(flatnessToDeg(v));
            }}
          />
        </div>

        <div className="pf-ctrl">
          <div className="pf-ctrl-hd">
            <label>Cull front</label>
            <span className="pf-ctrl-val">{cullFront}%</span>
          </div>
          <input
            className="pf-slider"
            type="range"
            min={0}
            max={100}
            step={1}
            value={cullFront}
            disabled={capturing || disabled}
            onChange={(e) => {
              const v = Number(e.target.value);
              setCullFront(v);
              app.setCullFront(v);
            }}
          />
        </div>

        <div className="pf-ctrl">
          <div className="pf-ctrl-hd">
            <label>Cull back</label>
            <span className="pf-ctrl-val">{cullBack}%</span>
          </div>
          <input
            className="pf-slider"
            type="range"
            min={0}
            max={100}
            step={1}
            value={cullBack}
            disabled={capturing || disabled}
            onChange={(e) => {
              const v = Number(e.target.value);
              setCullBack(v);
              app.setCullBack(v);
            }}
          />
        </div>
      </div>
    </div>
  );
}
